import React, { useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Skeleton } from "../Skeleton";
import Sparkline from "./Sparkline";
import { type Kpi } from "../../utils/operationsDashboard";

// The masthead: base tiles plus one per composed forecast, ordered in localStorage as a per-person preference
interface KpiRowProps {
  kpis: Kpi[];
  isLoading?: boolean;
  /** Everything off the row — removed base tiles as well as composed forecasts never added. */
  available: Array<{ id: string; name: string; kind: string }>;
  /** True when no composed forecast qualifies, which is worth saying rather than showing a bare list. */
  catalogueEmpty?: boolean;
  /** The saved order; can hold ids `kpis` has not loaded yet, so an early drag never drops those tiles. */
  layout: string[];
  onReorder: (ids: string[]) => void;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}

const TILE_CLASS =
  "flex h-48 min-w-0 flex-col gap-2 overflow-hidden rounded-eai-card border border-eai-border bg-eai-surface-card p-4 shadow-eai-card sm:h-56";

// Height sits on the tile, not on auto-rows, so one tall tile cannot resize the rest and the add-button row stays short
const ROW_CLASS = "mb-5 grid grid-cols-2 items-stretch gap-5 sm:grid-cols-3 lg:grid-cols-5";

interface KpiTileProps {
  kpi: Kpi;
  onRemove: (id: string) => void;
}

const KpiTile: React.FC<KpiTileProps> = ({ kpi, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: kpi.id,
  });
  // Red is reserved for the genuinely bad — an outstanding count is exactly that
  const bad = kpi.severity === "negative";
  const deltaUp = kpi.deltaDirection === "up";

  return (
    <div
      ref={setNodeRef}
      // pan-y rather than none: the tile drags sideways, but a touch meant to scroll the page still scrolls
      style={{ transform: CSS.Transform.toString(transform), transition, touchAction: "pan-y" }}
      {...attributes}
      {...listeners}
      // The card is its own handle, so role stays a grouping — a whole KPI tile is not a button
      role="group"
      className={`group relative cursor-grab active:cursor-grabbing ${TILE_CLASS} ${
        isDragging ? "z-10 cursor-grabbing opacity-60 shadow-eai-card-hover" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-x-2">
        <span className="eai-tag-pill-sm shrink-0">{kpi.tag}</span>
        {/* Hover-revealed so the resting row stays numbers */}
        <button
          type="button"
          onClick={() => onRemove(kpi.id)}
          aria-label={`Remove ${kpi.label}`}
          title="Remove"
          className="eai-card-reveal ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-eai-ink-faint transition-colors hover:bg-eai-danger-soft hover:text-eai-danger"
        >
          ×
        </button>
      </div>

      {/* Clamped, not truncated: a long composed-forecast name gets two lines before it gives up */}
      <p className="eai-card-title line-clamp-2 break-words">
        {kpi.label}
      </p>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {/* 24 up to 30, the scale's top: eai-h3's 20px read as a subtitle inside a 224px tile */}
        <span
          className={`font-outfit text-eai-page-title font-semibold leading-none md:text-eai-page-title-lg ${
            bad ? "text-eai-danger" : "text-eai-ink"
          }`}
        >
          {kpi.value}
        </span>
        {kpi.unit && (
          <span className="whitespace-nowrap font-inter text-eai-body-xs text-eai-ink-subtle">
            {kpi.unit}
          </span>
        )}
        {kpi.delta && (
          // Direction carries the colour; the baseline is a tooltip, since as copy it repeated on every tile
          <span
            title={kpi.deltaLabel}
            className={`ml-auto whitespace-nowrap font-outfit text-eai-body-xs font-semibold ${
              deltaUp ? "text-eai-cta-primary" : "text-eai-danger"
            }`}
          >
            {kpi.delta}
          </span>
        )}
      </div>

      {kpi.series && (
        <Sparkline
          values={kpi.series}
          label={`${kpi.label} trend`}
          tone={bad || kpi.deltaDirection === "down" ? "muted" : "accent"}
        />
      )}

      {/* The age sits at the meta line's right edge, as on every card below; items-end keeps it on the last line */}
      <div className="mt-auto flex items-end justify-between gap-2">
        <p
          title={kpi.detail}
          className="line-clamp-2 min-w-0 break-words font-inter text-eai-body-xs text-eai-ink-faint"
        >
          {kpi.detail}
        </p>
        {kpi.age && (
          <span className="shrink-0 whitespace-nowrap font-inter text-eai-body-xs text-eai-ink-faint">
            {kpi.age}
          </span>
        )}
      </div>
    </div>
  );
};

// The picker is a menu, not a modal: adding a tile is a one-click decision that should not take the page
interface AddWidgetButtonProps {
  available: Array<{ id: string; name: string; kind: string }>;
  catalogueEmpty?: boolean;
  onAdd: (id: string) => void;
}

const AddWidgetButton: React.FC<AddWidgetButtonProps> = ({ available, catalogueEmpty, onAdd }) => {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={wrapper} className="relative self-center justify-self-start">
      {/* On the card ground so it reads as part of the row, but a control rather than a tile */}
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Add widget"
        // The glyph is the whole control, so the name has to come from the label, not from the text
        aria-label="Add widget"
        className="flex h-16 w-16 items-center justify-center rounded-eai-card border border-dashed border-eai-border-soft-hover bg-eai-surface-card transition-colors hover:border-eai-cta-primary hover:bg-eai-checklist-lt"
      >
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-eai-cta-primary font-outfit text-eai-body-sm font-semibold leading-none text-white"
        >
          +
        </span>
      </button>

      {open && (
        // A float is an eai-card at mt-1.5, like every other menu in the app
        <div
          role="menu"
          className="eai-card absolute left-0 top-full z-30 mt-1.5 max-h-72 w-64 overflow-y-auto p-1.5"
        >
          {available.length === 0 ? (
            <p className="px-2.5 py-2 font-inter text-eai-body-xs leading-relaxed text-eai-ink-faint">
              {catalogueEmpty
                ? "Everything is on the row. A composed forecast appears here once it pairs a model against an actual feed."
                : "Everything is already on the row."}
            </p>
          ) : (
            available.map((option) => (
              <button
                key={option.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  onAdd(option.id);
                  setOpen(false);
                }}
                // Shaped like a Select's options, since that is the dropdown every other picker in the app is
                className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left transition-colors hover:bg-eai-surface"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-inter text-eai-body-sm text-eai-ink-soft">
                    {option.name}
                  </span>
                  <span className="mt-0.5 block font-inter text-eai-body-xs text-eai-ink-faint">
                    {option.kind}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const KpiRow: React.FC<KpiRowProps> = ({
  kpis,
  isLoading,
  available,
  catalogueEmpty,
  layout,
  onReorder,
  onAdd,
  onRemove,
}) => {
  // A short distance threshold, so a click on remove is never read as the start of a drag
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = layout.indexOf(String(active.id));
    const to = layout.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(layout, from, to));
  };

  if (isLoading) {
    return (
      <div className={ROW_CLASS}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className={TILE_CLASS}>
            <Skeleton className="h-4 w-20" radius="full" />
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-6 w-16" />
            <Skeleton className="hidden h-8 w-full sm:block" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className={ROW_CLASS}>
        <SortableContext items={kpis.map((kpi) => kpi.id)} strategy={rectSortingStrategy}>
          {kpis.map((kpi) => (
            <KpiTile key={kpi.id} kpi={kpi} onRemove={onRemove} />
          ))}
        </SortableContext>

        <AddWidgetButton available={available} catalogueEmpty={catalogueEmpty} onAdd={onAdd} />
      </div>
    </DndContext>
  );
};

export default KpiRow;
